import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOTPVerification } from '../hooks/useOTPVerification';
import { db, storage } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  calculateMinimumDeliveryDate,
  isValidDeliveryDate,
  formatDateForComparison,
  formatDateForDisplay,
} from '../utils/deliveryDateCalculator';
import { fetchCurrentAndNextYearHolidays } from '../utils/initializeHolidays';

// Sanitize phone numbers - remove invisible Unicode characters and extra spaces
// This prevents Firestore validation failures from copied text with hidden chars
const sanitizePhone = (phone) => {
  if (!phone) return '';
  // Remove invisible Unicode chars that can be pasted from other apps
  return phone.replace(/[\u200B-\u200D\uFEFF\u00A0\u2028\u2029\u200C]/g, '')
    .replace(/[^\d+\-\s()]/g, '')  // Keep digits, +, -, spaces, parens
    .trim();
};

export default function OrderForm({
  productName,
  pricing,
  quantitySettings = { enabled: false, minQty: 1, maxQty: 10 },
  templates = { enabled: false, items: [] },
  sizes = [], // Product variants/sizes
  paymentOptions = {}, // Payment configuration
  onSubmit,
  onCancel
}) {
  const { currentUser, userRole } = useAuth();
  const { verifyOTP, isVerifying } = useOTPVerification();
  const isReseller = currentUser && userRole === 'reseller';

  // Quantity state
  const [quantity, setQuantity] = useState(quantitySettings?.minQty || 1);

  // Size/Variant state
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(null);

  // Template state
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [customText, setCustomText] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);



  // Common fields
  const [customerName, setCustomerName] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [placerPhone, setPlacerPhone] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [samePhoneForBoth, setSamePhoneForBoth] = useState(false);
  const [sameNameForBoth, setSameNameForBoth] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');

  // Public-only fields
  const [deliveryMethod, setDeliveryMethod] = useState('STANDARD');
  const [pickMeUrl, setPickMeUrl] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

  // Reseller-only fields
  const [shipTo, setShipTo] = useState('CUSTOMER');
  const [resellerAddress, setResellerAddress] = useState('');
  const [designLockConfirmed, setDesignLockConfirmed] = useState(false);



  const finalPrice = useMemo(() => {
    // Use size price if selected, otherwise use base price
    let unitPrice = pricing.basePrice;

    // Use the size's price (priceDelta is the actual price for that size)
    if (selectedSizeIndex !== null && sizes[selectedSizeIndex]) {
      unitPrice = sizes[selectedSizeIndex].priceDelta || pricing.basePrice;
    }

    let price = unitPrice * quantity;

    // Apply product discount first (if any)
    if (pricing.discount && pricing.discount > 0) {
      price = price - (pricing.discount * quantity);
    }



    // Add delivery cost if not included and method is STANDARD
    // Note: PickMe delivery cost is paid separately/later
    if (!pricing.deliveryIncluded && pricing.deliveryPrice && deliveryMethod === 'STANDARD') {
      price += pricing.deliveryPrice;
    }

    return Math.max(0, price); // Ensure non-negative
  }, [pricing.basePrice, pricing.discount, pricing.deliveryIncluded, pricing.deliveryPrice, quantity, selectedSizeIndex, sizes, deliveryMethod]);

  // Calculate payment-specific fees (e.g. Koko Pay 13%)
  const kokoFee = selectedPaymentMethod === 'KOKO' ? finalPrice * 0.13 : 0;
  const effectiveTotal = finalPrice + kokoFee;
  const monthlyInstallment = selectedPaymentMethod === 'KOKO' ? effectiveTotal / 3 : 0;

  // Date picker state
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(true);

  // Fetch holidays from Firestore
  useEffect(() => {
    async function fetchHolidays() {
      try {
        const fetchedHolidays = await fetchCurrentAndNextYearHolidays();
        setHolidays(fetchedHolidays);
      } catch (error) {
        console.error('Error fetching holidays:', error);
        setHolidays([]); // Continue with empty holidays on error
      } finally {
        setHolidaysLoading(false);
      }
    }
    fetchHolidays();
  }, []);

  // Calculate minimum delivery date
  const minDeliveryDate = useMemo(() => {
    if (holidaysLoading) return null;
    return calculateMinimumDeliveryDate(new Date(), holidays);
  }, [holidays, holidaysLoading]);

  // Fetch reseller's information when "My Address" is selected
  useEffect(() => {
    async function fetchResellerInfo() {
      if (isReseller && shipTo === 'RESELLER') {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Pre-fill all reseller information
            setResellerAddress(userData.address || '');
            setAddress(userData.address || '');
            setCustomerName(userData.displayName || '');
            setCustomerEmail(userData.email || '');
            setPlacerPhone(userData.phone || '');
          }
        } catch (error) {
          console.error('Error fetching reseller information:', error);
        }
      } else if (isReseller && shipTo === 'CUSTOMER') {
        // Clear fields when switching back to customer address
        setAddress('');
        setResellerAddress('');
        // Note: We don't clear name, email, phone as they might be customer info
      }
    }
    fetchResellerInfo();
  }, [isReseller, shipTo, currentUser]);

  // Sync component names if "Same as your name" is checked
  useEffect(() => {
    if (sameNameForBoth) {
      setReceiverName(customerName);
    }
  }, [customerName, sameNameForBoth]);

  // Handle image upload for template - no limit, customers can upload any number
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Template must be selected
    if (selectedTemplateIndex === null) {
      alert('⚠️ Please select a template first');
      return;
    }

    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        // Create a unique path for each image
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(7);
        const path = `orders/temp-${timestamp}/template-images/${random}_${file.name}`;
        const storageRef = ref(storage, path);

        // Upload file
        await uploadBytes(storageRef, file);

        // Get download URL
        const url = await getDownloadURL(storageRef);

        return {
          storagePath: path,
          url: url,
          fileName: file.name
        };
      });

      const newImages = await Promise.all(uploadPromises);
      setUploadedImages(prev => [...prev, ...newImages]);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('❌ Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!customerName || !placerPhone || !address || !deliveryDate) {
      alert('⚠️ Please fill in all required fields');
      return;
    }

    if (!isReseller && deliveryMethod === 'PICKME' && !pickMeUrl) {
      alert('⚠️ Please provide Google Maps location link for PickMe delivery');
      return;
    }

    if (isReseller && !designLockConfirmed) {
      alert('⚠️ Please confirm that you understand the design cannot be changed after submission');
      return;
    }

    // Validate size/variant selection if product has enabled sizes
    const enabledSizes = sizes.filter(s => s.enabled);
    if (enabledSizes.length > 0 && selectedSizeIndex === null) {
      alert('⚠️ Please select a size/variant for your order');
      return;
    }

    // Validate template requirements - only for resellers (PUBLIC users select templates on tracking page)
    if (templates.enabled && templates.items.length > 0 && isReseller) {
      if (selectedTemplateIndex === null) {
        alert('⚠️ Please select a template for your order');
        return;
      }
    }

    // Validate delivery date using business rules
    const selectedDate = new Date(deliveryDate);

    if (!isValidDeliveryDate(selectedDate, new Date(), holidays)) {
      const minDateFormatted = minDeliveryDate ? formatDateForDisplay(minDeliveryDate) : '';
      alert(`⚠️ Invalid delivery date.\n\nPlease select a business day (Monday-Saturday, excluding holidays) that is at least 3 business days from today.\n\nEarliest available: ${minDateFormatted}`);
      return;
    }



    // OTP Verification for PUBLIC users (not resellers)
    if (!isReseller) {
      const otpVerified = await verifyOTP({
        email: customerEmail,
        phone: placerPhone,
      });

      if (!otpVerified) {
        // User cancelled or OTP verification failed
        return;
      }
    }



    // Phone numbers are already sanitized at input time, just trim
    const trimmedPlacerPhone = placerPhone?.trim() || '';
    const trimmedReceiverPhone = (samePhoneForBoth ? placerPhone : receiverPhone)?.trim() || '';

    // Calculate effective unit price based on variant selection
    let effectiveUnitPrice = pricing.basePrice;
    if (enabledSizes.length > 0 && selectedSizeIndex !== null && sizes[selectedSizeIndex]) {
      effectiveUnitPrice = sizes[selectedSizeIndex].priceDelta || pricing.basePrice;
    }

    // Prepare order data
    const orderData = {
      productName: productName, // Add product name to fix Legacy Order issue
      channel: isReseller ? 'RESELLER' : 'PUBLIC',
      isResellerOrder: isReseller,
      resellerId: isReseller ? currentUser.uid : null,
      placerPhone: trimmedPlacerPhone,
      receiverPhone: trimmedReceiverPhone,
      samePhoneForBoth: samePhoneForBoth,
      phone: trimmedPlacerPhone, // Keep for backward compatibility
      customerPhone: trimmedPlacerPhone, // Keep for backward compatibility
      fulfillment: {
        shipTo: isReseller ? shipTo : 'CUSTOMER',
        recipient: {
          name: customerName,
          receiverName: receiverName || customerName, // Use receiverName if provided, otherwise fallback to customerName
          phone: trimmedPlacerPhone, // This is for display purposes
          email: customerEmail,
          address: address,
        },
        requestedDeliveryDate: deliveryDate,
        notes: notes || null,
      },
      delivery: isReseller
        ? { method: 'STANDARD' }
        : {
          method: deliveryMethod,
          pickme:
            deliveryMethod === 'PICKME'
              ? {
                mapsUrl: pickMeUrl,
                declaredWithin20km: true,
              }
              : null,
        },
      payment: isReseller
        ? {
          method: 'WEEKLY_SETTLEMENT',
          status: 'PENDING', // Will be updated when weekly payment is made
        }
        : {
          method: selectedPaymentMethod || 'MANUAL_VERIFICATION', // Default if not selected
          status: 'PENDING'
        },
      priceAtOrder: {
        amount: effectiveUnitPrice,
        currency: pricing.currency,
        listType: pricing.listType,
        deliveryIncluded: pricing.deliveryIncluded,
        deliveryPrice: pricing.deliveryPrice || 0,
        quantity: quantity,
        unitPrice: effectiveUnitPrice,
        subtotal: effectiveUnitPrice * quantity,
        // Include size/variant info if selected
        ...(selectedSizeIndex !== null && sizes[selectedSizeIndex] && {
          size: {
            label: sizes[selectedSizeIndex].label,
            priceDelta: sizes[selectedSizeIndex].priceDelta || 0,
          },
        }),

        // Always include final amount (includes delivery, discounts, Koko fee, etc.)
        finalAmount: effectiveTotal,
        // Include payment fee detail
        ...(kokoFee > 0 && {
          paymentFee: kokoFee,
          paymentFeeReason: 'Koko Pay Service Fee (13%)'
        }),
      },

      // Template data - includes quantity, images (optional), and customer note (optional)
      ...(templates.enabled && selectedTemplateIndex !== null && {
        template: {
          name: templates.items[selectedTemplateIndex].name,
          quantity: quantity, // Include quantity ordered
          images: uploadedImages, // Array of uploaded images (can be empty)
          customerNote: customText.trim() || null, // Customer's special instructions
        },
      }),
    };

    onSubmit(orderData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1001] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className={`p-6 border-b ${isReseller ? 'bg-blue-50' : 'bg-green-50'}`}>
          <h2 className="text-2xl font-bold text-gray-900">
            {isReseller ? '🏢 Reseller Order Details' : '👤 Order Details'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {isReseller
              ? 'Fill in the customer details for this order'
              : 'Enter your information to complete the order'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Quantity Selector */}
          {quantitySettings?.enabled && (
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
              <label className="block text-sm font-medium text-gray-800 mb-3">
                {productName || 'Product'} - Select Quantity
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(quantitySettings.minQty, quantity - 1))}
                  disabled={quantity <= quantitySettings.minQty}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#E85D44] text-[#E85D44] rounded-lg font-bold text-xl hover:bg-[#E85D44] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#E85D44]"
                >
                  −
                </button>
                <div className="flex-1 text-center">
                  <div className="text-3xl font-bold text-gray-900">{quantity}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Min: {quantitySettings.minQty} | Max: {quantitySettings.maxQty}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(quantitySettings.maxQty, quantity + 1))}
                  disabled={quantity >= quantitySettings.maxQty}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#E85D44] text-[#E85D44] rounded-lg font-bold text-xl hover:bg-[#E85D44] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#E85D44]"
                >
                  +
                </button>
              </div>
            </div>
          )}

          {/* Template Selection - Only for Resellers (PUBLIC users select on tracking page) */}
          {templates.enabled && templates.items.length > 0 && isReseller && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-3">
                  Select a Template *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {templates.items.map((template, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setSelectedTemplateIndex(index);
                        setUploadedImages([]);
                        setCustomText('');
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${selectedTemplateIndex === index
                        ? 'border-purple-500 bg-purple-100'
                        : 'border-gray-300 bg-white hover:border-purple-300'
                        }`}
                    >
                      <div className="font-semibold text-gray-900">{template.name}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {template.description || 'Select this template'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {selectedTemplateIndex !== null && (
                <div className="space-y-4 border-t border-purple-200 pt-4">
                  {/* Multi-Image Upload - Optional, No Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Upload Reference Photos (Optional)
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      📷 Upload any photos you want us to use. You can also send photos via WhatsApp later.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImages}
                      className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-purple-500 file:text-white hover:file:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {uploadingImages && (
                      <p className="text-sm text-purple-600 mt-2">Uploading images...</p>
                    )}
                  </div>

                  {/* Uploaded Images Preview */}
                  {uploadedImages.length > 0 && (
                    <div>
                      <p className="text-sm text-green-600 mb-2">✓ {uploadedImages.length} photo(s) uploaded</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {uploadedImages.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={img.url}
                              alt={`Upload ${index + 1}`}
                              className="w-full h-24 object-cover rounded border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Customer Note Field - Optional */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Special Instructions / Note (Optional)
                    </label>
                    <textarea
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter any special instructions, names to print, text content, or other details..."
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reseller Ship-To Selection */}
          {isReseller && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">Ship To</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="CUSTOMER"
                    checked={shipTo === 'CUSTOMER'}
                    onChange={(e) => setShipTo(e.target.value)}
                    className="mr-2"
                  />
                  <span>Customer Address</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="RESELLER"
                    checked={shipTo === 'RESELLER'}
                    onChange={(e) => setShipTo(e.target.value)}
                    className="mr-2"
                  />
                  <span>My Address (Reseller)</span>
                </label>
              </div>
            </div>
          )}

          {/* Contact Information & Delivery Details */}
          <div className="space-y-6">
            <h3 className="font-semibold text-gray-900 border-b pb-2">
              {isReseller && shipTo === 'RESELLER' ? 'Order Contact Information' : 'Contact Information'}
            </h3>

            {/* Placer Info */}
            <div className="grid grid-cols-1 gap-4">
              {/* Placer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isReseller ? 'Your Name *' : 'Your Full Name *'}
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  disabled={isReseller && shipTo === 'RESELLER'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="John Doe"
                />
                {isReseller && shipTo === 'RESELLER' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Using your reseller account name
                  </p>
                )}
              </div>

              {/* Placer Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your WhatsApp Number *
                </label>
                <input
                  type="tel"
                  value={placerPhone}
                  onChange={(e) => setPlacerPhone(sanitizePhone(e.target.value))}
                  required
                  disabled={isReseller && shipTo === 'RESELLER'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                  placeholder="+94 712 345 678"
                />
                <p className="text-xs text-green-600 font-medium mt-1">
                  ✨ You will get WhatsApp updates!
                </p>
              </div>

              {/* Email Address (Moved from Delivery Details) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address (Optional)
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  disabled={isReseller && shipTo === 'RESELLER'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Isolated Receiver Box */}
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 mt-6 relative">
              <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-md shadow-sm">
                Delivery Details
              </div>

              <div className="space-y-4 pt-2">
                {/* Receiver Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receiver Name {!isReseller && '(if different)'}
                  </label>

                  <div className="mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sameNameForBoth}
                        onChange={(e) => setSameNameForBoth(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Same as your name</span>
                    </label>
                  </div>

                  {!sameNameForBoth && (
                    <input
                      type="text"
                      value={receiverName}
                      onChange={(e) => setReceiverName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder={isReseller ? "Customer's Name" : "Gift recipient (optional)"}
                    />
                  )}
                  {!sameNameForBoth && (
                    <p className="text-xs text-gray-500 mt-1">
                      {isReseller ? 'The person who will receive this order' : 'Leave empty if you are the receiver'}
                    </p>
                  )}
                </div>

                {/* Receiver Phone + Checkbox */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receiver Phone Number
                  </label>

                  <div className="mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={samePhoneForBoth}
                        onChange={(e) => setSamePhoneForBoth(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Same as your number</span>
                    </label>
                  </div>

                  {!samePhoneForBoth && (
                    <input
                      type="tel"
                      value={receiverPhone}
                      onChange={(e) => setReceiverPhone(sanitizePhone(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="+94 712 345 678"
                    />
                  )}
                  {!samePhoneForBoth && (
                    <p className="text-xs text-gray-500 mt-1">
                      For gift orders - receiver won't get SMS notifications
                    </p>
                  )}
                </div>



                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shipping Address *
                  </label>
                  <textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    placeholder="123, Main Street, Colombo"
                    disabled={isReseller && shipTo === 'RESELLER' && resellerAddress}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Method (Public Only) */}
          {!isReseller && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <label className="block text-sm font-medium text-gray-700">Delivery Method *</label>
              <div className="space-y-2">
                <label className="flex items-start">
                  <input
                    type="radio"
                    value="STANDARD"
                    checked={deliveryMethod === 'STANDARD'}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                    className="mr-2 mt-1"
                  />
                  <div>
                    <div className="font-medium">
                      Standard Delivery {pricing.deliveryIncluded ? '(Free)' : pricing.deliveryPrice ? `(${pricing.currency} ${pricing.deliveryPrice.toLocaleString()})` : ''}
                    </div>
                    <div className="text-xs text-gray-600">Delivery within 2-3 business days</div>
                  </div>
                </label>
                <label className="flex items-start">
                  <input
                    type="radio"
                    value="PICKME"
                    checked={deliveryMethod === 'PICKME'}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                    className="mr-2 mt-1"
                  />
                  <div>
                    <div className="font-medium">PickMe Delivery (within 20km only - Delivery Cost needs to bear by you)</div>
                    <div className="text-xs text-gray-600">Fee will be quoted on WhatsApp</div>
                  </div>
                </label>
              </div>

              {deliveryMethod === 'PICKME' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Maps Location Link *
                  </label>
                  <input
                    type="url"
                    value={pickMeUrl}
                    onChange={(e) => setPickMeUrl(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://maps.google.com/..."
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ PickMe fee is separate and will be confirmed on WhatsApp. If beyond 20km, we'll use Standard delivery.
                  </p>
                </div>
              )}
            </div>

          )}

          {/* Payment Method Selection (Public Only & If Enabled) */}
          {!isReseller && paymentOptions?.enabled && (
            (() => {
              const methods = paymentOptions.paymentMethods || {};
              // Only show if at least one method is enabled
              if (!methods.onlineTransfer && !methods.creditDebit && !methods.kokoPay && !methods.cod) return null;

              return (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Select Payment Method (Optional)</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {methods.onlineTransfer && (
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('BANK_TRANSFER')}
                        className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${selectedPaymentMethod === 'BANK_TRANSFER'
                          ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
                          : 'border-gray-200 bg-white hover:border-blue-400 hover:bg-gray-50'
                          }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl flex-shrink-0">
                          🏦
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Bank Transfer</div>
                          <div className="text-xs text-gray-500 mt-0.5">Upload receipt on WhatsApp</div>
                        </div>
                      </button>
                    )}

                    {methods.creditDebit && (
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('CARD')}
                        className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${selectedPaymentMethod === 'CARD'
                          ? 'border-purple-600 bg-purple-50 ring-1 ring-purple-600'
                          : 'border-gray-200 bg-white hover:border-purple-400 hover:bg-purple-50'
                          }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-xl flex-shrink-0">
                          💳
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Card Payment</div>
                          <div className="text-xs text-gray-500 mt-0.5">Visa, Master (PayHere)</div>
                        </div>
                      </button>
                    )}

                    {methods.kokoPay && (
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('KOKO')}
                        className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${selectedPaymentMethod === 'KOKO'
                          ? 'border-pink-600 bg-pink-50 ring-1 ring-pink-600'
                          : 'border-gray-200 bg-white hover:border-pink-300 hover:bg-pink-50'
                          }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-xl flex-shrink-0">
                          🛍️
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">KOKO Pay</div>
                          <div className="text-xs text-gray-500 mt-0.5">Pay in 3 installments</div>
                        </div>
                      </button>
                    )}

                    {methods.cod && (
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentMethod('COD')}
                        className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${selectedPaymentMethod === 'COD'
                          ? 'border-green-600 bg-green-50 ring-1 ring-green-600'
                          : 'border-gray-200 bg-white hover:border-green-400 hover:bg-green-50'
                          }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-xl flex-shrink-0">
                          💵
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Cash on Delivery</div>
                          <div className="text-xs text-gray-500 mt-0.5">Pay upon receiving</div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              );
            })()
          )}

          {/* Delivery Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Requested Delivery Date *
            </label>
            {holidaysLoading ? (
              <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                <span className="text-gray-500">Loading available dates...</span>
              </div>
            ) : (
              <>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  required
                  min={minDeliveryDate ? formatDateForComparison(minDeliveryDate) : new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="text-xs text-gray-600 mt-1 space-y-1">
                  <p>
                    📅 Earliest available: <strong>{minDeliveryDate ? formatDateForDisplay(minDeliveryDate) : 'Calculating...'}</strong>
                  </p>
                  <p className="text-gray-500">
                    ℹ️ Business days only (Mon-Sat, excluding holidays & Sundays)
                  </p>
                  {holidays.length > 0 && (
                    <p className="text-gray-500">
                      🎉 {holidays.length} holidays excluded
                    </p>
                  )}
                </div>
              </>
            )}
          </div>



          {/* Size/Variant Selection */}
          {sizes.filter(s => s.enabled).length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <label className="block text-sm font-medium text-gray-800 mb-3">
                Select Size / Variant *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sizes.map((size, index) => {
                  if (!size.enabled) return null;

                  const isSelected = selectedSizeIndex === index;
                  const sizePrice = size.priceDelta || pricing.basePrice;

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedSizeIndex(index)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${isSelected
                        ? 'border-blue-600 bg-blue-100 shadow-md'
                        : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
                        }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{size.label}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            LKR {sizePrice.toLocaleString()}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="ml-2">
                            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reseller Design Lock Confirmation */}
          {isReseller && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={designLockConfirmed}
                  onChange={(e) => setDesignLockConfirmed(e.target.checked)}
                  required
                  className="mr-2 mt-1"
                />
                <span className="text-sm font-medium">
                  I confirm that this design <strong>cannot be changed after submission</strong>
                </span>
              </label>
            </div>
          )}

          {/* Price Summary - Hidden for resellers */}
          {!isReseller && (
            <div className="bg-gray-100 p-4 rounded-lg space-y-2">
              {/* Affiliate Discount Banner */}


              {/* Price Breakdown */}
              {quantitySettings?.enabled ? (
                <>
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Unit Price:</span>
                    <span>
                      {pricing.currency} {((selectedSizeIndex !== null && sizes[selectedSizeIndex]) ? (sizes[selectedSizeIndex].priceDelta || pricing.basePrice) : pricing.basePrice).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-gray-600">
                    <span>Quantity:</span>
                    <span>× {quantity}</span>
                  </div>
                  <div className="flex justify-between items-center text-gray-700 font-medium">
                    <span>Subtotal:</span>
                    <span>{pricing.currency} {(((selectedSizeIndex !== null && sizes[selectedSizeIndex]) ? (sizes[selectedSizeIndex].priceDelta || pricing.basePrice) : pricing.basePrice) * quantity).toLocaleString()}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between items-center text-gray-600">
                  <span>Subtotal:</span>
                  <span>
                    {pricing.currency} {((selectedSizeIndex !== null && sizes[selectedSizeIndex]) ? (sizes[selectedSizeIndex].priceDelta || pricing.basePrice) : pricing.basePrice).toLocaleString()}
                  </span>
                </div>
              )}

              {pricing.discount && pricing.discount > 0 && (
                <div className="flex justify-between items-center text-[#E85D44]">
                  <span>Product Discount:</span>
                  <span>- {pricing.currency} {(pricing.discount * quantity).toLocaleString()}</span>
                </div>
              )}

              {pricing.discountReason && (
                <div className="text-right text-xs text-[#E85D44] font-medium mt-1">
                  🎁 {pricing.discountReason}
                </div>
              )}



              {!pricing.deliveryIncluded && pricing.deliveryPrice > 0 && (
                <div className="flex justify-between items-center text-gray-600">
                  <span>Delivery:</span>
                  <span>{pricing.currency} {pricing.deliveryPrice.toLocaleString()}</span>
                </div>
              )}

              {/* Koko Pay Fee Display */}
              {kokoFee > 0 && (
                <div className="flex justify-between items-center text-pink-600">
                  <span>Koko Pay Fee (13%):</span>
                  <span>+ {pricing.currency} {kokoFee.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                </div>
              )}

              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Amount:</span>
                  <div className="text-right">

                    <span className="text-xl font-bold text-blue-600">
                      {pricing.currency} {effectiveTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* Monthly Installment Display */}
                {monthlyInstallment > 0 && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-gray-300">
                    <span className="font-medium text-pink-600">Monthly Pay (x3):</span>
                    <span className="font-bold text-pink-600">
                      {pricing.currency} {monthlyInstallment.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-600 mt-1">
                {pricing.deliveryIncluded && '(Free delivery included)'}
                {!pricing.deliveryIncluded && pricing.deliveryPrice > 0 && '(Delivery charge included in total)'}
                {deliveryMethod === 'PICKME' && ' + PickMe fee (TBD)'}
              </p>
            </div>
          )}
        </form>

        {/* Footer Actions */}
        <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isVerifying}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isVerifying
              ? 'Verifying...'
              : isReseller
                ? 'Submit Order (5-min grace period)'
                : 'Continue to Verification'}
          </button>
        </div>
      </div >
    </div >
  );
}
